import React, { useEffect } from "react";
import { Switch, Route, HashRouter as Router, NavLink, Redirect } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "@fortawesome/fontawesome-free/css/all.css";
import "./App.css";

import { Navbar, Nav, Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faCog, faUsers, faImage, faEnvelope, faSignOutAlt, faInfo } from "@fortawesome/free-solid-svg-icons";

// Auth
import { useAuth, useThemeStyles, ThemeProvider, useTheme } from '@repo/shared';
import LoginPage from './components/login2/LoginPage';
import ProtectedRoute from './components/login2/ProtectedRoute';
import { AcademicYearProvider } from './contexts/AcademicYearContext';

// Theme (from shared package)

// Component Imports
import ClassAttendance from "./components/class/Attendance";
import Lesson from "./components/class/Lesson";
import Home from "./components/home";
import Plans from "./components/plans";
import Parent from "./components/parent/Parent";
import Parents from "./components/parent/ParentList";
import Class from "./components/class/Class";
import ClassesList from "./components/class/ClassList";
import Student from "./components/student/Student";
import StudentList from "./components/student/StudentList";
import School from "./components/school/School";
import SchoolList from "./components/school/SchoolList";
import UserList from "./components/user/UserList";
import SchoolYears from './components/config/SchoolYears';
import EmailConfig from './components/config/EmailConfig';
import ConfigEdit from './components/config/ConfigEdit';
import EmailTemplates from './components/config/EmailTemplates';
import Images from './components/images/ImageOrganiser';
import Event from './components/event/Event';
import EventsList from "./components/event/EventList";
import EventAttendance from './components/event/EventAttendance';
import EventLesson from "./components/event/Lesson";
import Tutorials from './components/config/Tutorials';

const logoStyle = {
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  objectFit: 'cover'
};

// =====================================================================
// Navigation component with role-based routing
// =====================================================================
const Navigation = ({ user, logout }) => {
  const { appTheme } = useTheme();
  const renderNavLinks = () => {
    if (user.role === 'admin') {
      return (
        <>
          <Nav.Item><Nav.Link as={NavLink} to="/home">Home</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link as={NavLink} to="/classes">Classes</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link as={NavLink} to="/students">Students</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link as={NavLink} to="/schools">Schools</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link as={NavLink} to="/plans">Plans</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link as={NavLink} to="/parents">Parents</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link as={NavLink} to="/events">Special Events</Nav.Link></Nav.Item>
        </>
      );
    } else if (user.role === 'superuser') {
      return (
        <>
          <Nav.Item><Nav.Link as={NavLink} to="/home">Home</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link as={NavLink} to="/classes">Classes</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link as={NavLink} to="/students">Students</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link as={NavLink} to="/schools">Schools</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link as={NavLink} to="/plans">Plans</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link as={NavLink} to="/parents">Parents</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link as={NavLink} to="/events">Special Events</Nav.Link></Nav.Item>
        </>
      );
    } else if (user.teacher) {
      return (
        <>
          <Nav.Item><Nav.Link as={NavLink} to="/home">Home</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link as={NavLink} to="/classes">Classes</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link as={NavLink} to="/students">Students</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link as={NavLink} to="/schools">Schools</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link as={NavLink} to="/plans">Plans</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link as={NavLink} to="/parents">Parents</Nav.Link></Nav.Item>
        </>
      );
    }
    else if (user.role === 'artist' || user.role === 'artist-viewer') {
      return (
        <Nav.Item><Nav.Link as={NavLink} to="/images">Images</Nav.Link></Nav.Item>
      );
    }
    return null;
  };

  return (
    <Navbar bg="primary" variant="dark" expand="lg" className="px-3 shadow-sm">
      <Navbar.Brand as={NavLink} to="/">
        <img 
          alt="logo"
          src={new URL(`./images/${appTheme?.logo || 'logo-default.png'}`, import.meta.url).href}
          height="30"
          className="d-inline-block align-top"
          style={logoStyle}/>
      </Navbar.Brand>
      <Navbar.Toggle aria-controls="main-navbar-nav" />
      <Navbar.Collapse id="main-navbar-nav">
        <Nav className="me-auto">
          {renderNavLinks()}
        </Nav>
        <Nav>
          <Dropdown as={Nav.Item}>
            <Dropdown.Toggle as={Nav.Link} id="user-dropdown">
              <FontAwesomeIcon icon={faUser} className="me-2" />
              <span>{user?.username} ({user?.role})</span>
            </Dropdown.Toggle>
            <Dropdown.Menu align="end">
              {user.role === 'admin' && (
                <>
                  <Dropdown.Item as={NavLink} to="/schoolyears"><FontAwesomeIcon icon={faCog} className="me-2" />Years</Dropdown.Item>
                  <Dropdown.Item as={NavLink} to="/emailConfig"><FontAwesomeIcon icon={faCog} className="me-2" />Email Config</Dropdown.Item>
                  <Dropdown.Item as={NavLink} to="/config"><FontAwesomeIcon icon={faCog} className="me-2" />Config</Dropdown.Item>
                  <Dropdown.Item as={NavLink} to="/users"><FontAwesomeIcon icon={faUsers} className="me-2" />Users</Dropdown.Item>
                  <Dropdown.Item as={NavLink} to="/images"><FontAwesomeIcon icon={faImage} className="me-2" />Images</Dropdown.Item>
                  <Dropdown.Item as={NavLink} to="/emailTemplates"><FontAwesomeIcon icon={faEnvelope} className="me-2" />Email Templates</Dropdown.Item>
                  
                </>
              )}
              {user.role === 'superuser' && (
                <>
                  <Dropdown.Item as={NavLink} to="/images"><FontAwesomeIcon icon={faImage} className="me-2" />Images</Dropdown.Item>
                </>
              )}
              <Dropdown.Divider />
              <Dropdown.Item as={NavLink} to="/tutorials"><FontAwesomeIcon icon={faInfo} className="me-2" />Tutorials</Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={logout} className="text-danger">
                <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />Logout
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Nav>
      </Navbar.Collapse>
    </Navbar>
  );
};

// =====================================================================
// Inner App component - has access to ThemeContext
// =====================================================================
function AppContent({ user, logout }) {
  const { appTheme } = useTheme();

  // Apply theme CSS variables
  useThemeStyles();
  
  useEffect(() => {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        link.type = 'image/png';
        document.getElementsByTagName('head')[0].appendChild(link);
      }
      link.href = new URL(`./images/${appTheme?.logo || 'logo-default.png'}`, import.meta.url).href;
    }, [appTheme]);

  
  return (
    <div className="App">
      <Router>
        <Navigation user={user} logout={logout} />
        <div className="container-fluid">
          <Switch>
            <Route
              exact
              path={[`/`, '/app']}
              render={() => {
                if (user.role === 'artist-viewer') {
                  return <Redirect to='/images' />;
                }
                return <Redirect to='/home' />;
              }}
            />

            {/* Home - accessible to all authenticated users */}
            <Route exact path="/home" render={() => <Home user={user} />} />

            {/* Parents - admin and teacher only */}
            <ProtectedRoute exact path="/parents" component={Parents} requiredRoles={['admin', 'superuser', 'user']} />
            <ProtectedRoute exact path="/parent" component={Parent} requiredRoles={['admin', 'superuser', 'user']} />
            <ProtectedRoute path="/parent/:id" component={Parent} requiredRoles={['admin', 'superuser', 'user']} />

            {/* Plans - admin and teacher only */}
            <ProtectedRoute exact path="/plans" component={Plans} requiredRoles={['admin', 'superuser', 'user']} />

            {/* Classes - admin and teacher only */}
            <ProtectedRoute exact path="/classes" component={ClassesList} requiredRoles={['admin', 'superuser', 'user']} />
            <ProtectedRoute exact path="/class" component={Class} requiredRoles={['admin', 'superuser', 'user']} />
            <ProtectedRoute path="/class/:id" component={Class} requiredRoles={['admin', 'superuser', 'user']} />
            <ProtectedRoute path="/attendance/class/:id" component={ClassAttendance} requiredRoles={['admin', 'superuser', 'user']} />
            <ProtectedRoute path="/lesson/class/:id" component={Lesson} requiredRoles={['admin', 'superuser', 'user']} />

            {/* Students - admin and teacher only */}
            <ProtectedRoute exact path="/students" component={StudentList} requiredRoles={['admin', 'superuser', 'user']} />
            <ProtectedRoute exact path="/student" component={Student} requiredRoles={['admin', 'superuser', 'user']} />
            <ProtectedRoute path="/student/:id" component={Student} requiredRoles={['admin', 'superuser', 'user']} />

            {/* Schools - admin and teacher only */}
            <ProtectedRoute exact path="/schools" component={SchoolList} requiredRoles={['admin', 'superuser', 'user']} />
            <ProtectedRoute exact path="/school" component={School} requiredRoles={['admin', 'superuser', 'user']} />
            <ProtectedRoute path="/school/:id" component={School} requiredRoles={['admin', 'superuser', 'user']} />

            {/* Events - admin only */}
            <ProtectedRoute exact path="/events" component={EventsList} requiredRoles={['admin']} />
            <ProtectedRoute exact path="/event" component={Event} requiredRoles={['admin']} />
            <ProtectedRoute path="/event/:id" component={Event} requiredRoles={['admin']} />
            <ProtectedRoute path="/attendance/event/:id" component={EventAttendance} requiredRoles={['admin']} />
            <ProtectedRoute path="/lesson/event/:id" component={EventLesson} requiredRoles={['admin']} />

            {/* Admin Pages */}
            <ProtectedRoute exact path="/users" component={UserList} requiredRoles={['admin']} />
            <ProtectedRoute exact path="/images" component={Images} requiredRoles={['admin', 'superuser', 'artist', 'artist-viewer']} />
            <ProtectedRoute exact path="/schoolyears" component={SchoolYears} requiredRoles={['admin']} />
            <ProtectedRoute exact path="/emailConfig" component={EmailConfig} requiredRoles={['admin']} />
            <ProtectedRoute exact path="/config" component={ConfigEdit} requiredRoles={['admin']} />
            <ProtectedRoute exact path="/emailTemplates" component={EmailTemplates} requiredRoles={['admin']} />
            <ProtectedRoute exact path="/tutorials" component={Tutorials} />

            {/* Catch-all route for 404 pages */}
            <Route path="*">
              <div><h2>404 Not Found</h2></div>
            </Route>
          </Switch>
        </div>
      </Router>
    </div>
  );
}

// =====================================================================
// Main App component
// =====================================================================
function App() {
  const { isAuthenticated, user, logout } = useAuth();

  // Debug: log auth state changes
  useEffect(() => {
    console.log('Auth state changed:', { isAuthenticated, user: user?.username, role: user?.role });
  }, [isAuthenticated, user]);

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return (
    <ThemeProvider>
      <LoginPage onLoginSuccess={(status, response) => console.log('Login successful:', status, response)} />
    </ThemeProvider>
    );
  }

  // Show authenticated app with all routes wrapped in ThemeProvider and AcademicYearProvider
  return (
    <ThemeProvider>
      <AcademicYearProvider>
        <AppContent user={user} logout={logout} />
      </AcademicYearProvider>
    </ThemeProvider>
  );
}

export default App;
